import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Typography,
  Divider,
  Avatar,
  Chip,
  Badge,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useMessagingNotifications } from '../../context/MessagingContext';
import { useRequestsNotifications } from '../../context/RequestsNotificationContext';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Explore as ExploreIcon,
  Person as PersonIcon,
  Dashboard as DashboardIcon,
  VideoLibrary as VideoLibraryIcon,
  Assignment as AssignmentIcon,
  Message as MessageIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Home as HomeIcon,
  AdminPanelSettings as AdminIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { getAvatarGradient, getInitials } from '../../utils/avatarUtils';

interface MobileNavigationProps {
  user: any;
  onLogout: () => void;
  onLoginClick?: () => void;
}

const MobileNavigation: React.FC<MobileNavigationProps> = ({ user, onLogout, onLoginClick }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { unreadCount } = useMessagingNotifications();
  const { pendingCount, unseenStatusCount } = useRequestsNotifications();

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleNavigation = (path: string) => {
    if (path === '/login' && onLoginClick) {
      onLoginClick();
      setDrawerOpen(false);
    } else {
      navigate(path);
      setDrawerOpen(false);
    }
  };

  const handleLogoutAndClose = () => {
    onLogout();
    setDrawerOpen(false);
    // Note: logout() function already handles redirect, so no need to navigate here
  };

  // Don't render on desktop
  if (!isMobile) {
    return null;
  }

  const isCreator = user?.role === 'creator' || user?.role === 'host' || Boolean(user?.creator_type);
  const menuItems = user ? (
    user.role === 'admin' ? [
      // Admin user - only show Admin
      { text: 'Admin', icon: <AdminIcon />, path: '/admin' },
    ] : [
      { text: 'Home', icon: <HomeIcon />, path: '/' },
      { text: 'Explore', icon: <ExploreIcon />, path: '/explore' },
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
      { text: 'Profile', icon: <PersonIcon />, path: '/profile' },
      { text: 'My Requests', icon: <AssignmentIcon />, path: '/my-requests' },
      ...(isCreator ? [
        { text: 'My Shoots', icon: <VideoLibraryIcon />, path: '/my-shoots' },
        { text: 'Requests Received', icon: <AssignmentIcon />, path: '/requests?tab=received' },
      ] : []),
      { text: 'Messages', icon: <MessageIcon />, path: '/messages' },
      { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
    ]
  ) : [
    { text: 'Home', icon: <HomeIcon />, path: '/' },
    { text: 'Explore', icon: <ExploreIcon />, path: '/explore' },
    { text: 'Sign In', icon: <LoginIcon />, path: '/login' },
  ];

  return (
    <>
      <IconButton
        color="inherit"
        aria-label="Open navigation menu"
        edge="start"
        onClick={handleDrawerToggle}
        sx={{ 
          color: 'white',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          }
        }}
      >
        <MenuIcon />
      </IconButton>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            backgroundColor: '#f8f9fa',
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box
            sx={{
              p: 2,
              backgroundColor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Guest Stream
            </Typography>
            <IconButton
              aria-label="Close navigation menu"
              onClick={handleDrawerToggle}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* User Info */}
          {user && (
            <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar 
                  src={user.profile_image || undefined}
                  alt={user.display_name || user.username || 'Profile'}
                  sx={{ 
                    width: 52, 
                    height: 52, 
                    background: user.profile_image ? undefined : getAvatarGradient(user.display_name || user.username || '').gradient,
                    fontSize: '1.3rem',
                    fontWeight: 700,
                    boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                    border: '2px solid rgba(255,255,255,0.3)',
                    letterSpacing: '0.5px',
                    textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                  }}
                >
                  {getInitials(user.display_name || user.username || '')}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {user.display_name || user.username}
                  </Typography>
                  <Chip
                    size="small"
                    label={(user.role || 'user').charAt(0).toUpperCase() + (user.role || 'user').slice(1)}
                    sx={{
                      backgroundColor: 'rgba(45, 32, 112, 0.1)',
                      color: 'primary.main',
                      fontWeight: 500,
                      textTransform: 'capitalize',
                    }}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {/* Navigation Items */}
          <List sx={{ flex: 1, py: 1 }}>
            {menuItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    py: 1.5,
                    px: 3,
                    '&:hover': {
                      backgroundColor: 'rgba(45, 32, 112, 0.08)',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: 'primary.main',
                      minWidth: 40,
                    }}
                  >
                    {item.text === 'Messages' ? (
                      <Badge badgeContent={unreadCount} color="error" max={99}>
                        {item.icon}
                      </Badge>
                    ) : item.text === 'My Requests' ? (
                      <Badge badgeContent={unseenStatusCount} color="error" max={99}>
                        {item.icon}
                      </Badge>
                    ) : item.text === 'Requests Received' ? (
                      <Badge badgeContent={pendingCount} color="error" max={99}>
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: 500,
                      color: '#333',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          {/* Logout Button */}
          {user && (
            <>
              <Divider />
              <ListItem disablePadding>
                <ListItemButton
                  onClick={handleLogoutAndClose}
                  sx={{
                    py: 2,
                    px: 3,
                    color: '#f44336',
                    '&:hover': {
                      backgroundColor: 'rgba(244, 67, 54, 0.08)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: '#f44336', minWidth: 40 }}>
                    <LogoutIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Logout"
                    primaryTypographyProps={{
                      fontWeight: 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default MobileNavigation;
